import { Breadcrumb, Button } from "pepsico-ds";
import { useContext, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { SummaryField } from "../../../components/jsonForms/summaryField/SummaryField";
import { GlobalConfigContext } from "../../../context/GlobalConfigContext";
import { paths } from "../../../routes/paths";
import { convertDateToLocaleTz } from "../../../utils/commons";
import { formatCampaignType } from "../../../utils/formatters";
import { DifferenceDisplay } from "../../../components/management/historyLog/DifferenceDisplay";
import {
  getCampaignTitleShort,
  parseEntityData,
} from "../../../utils/historyLogUtils";
import "./HistoryLogsDetailPage.scss";

export default function HistoryLogsDetailPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const context = useContext(GlobalConfigContext) || {};
  const { programTimezone = "UTC" } = context;
  const dataReceived = location?.state?.data;

  // Extract campaign title short using utility function
  const campaignTitleShort = getCampaignTitleShort(dataReceived?.entity);

  // Parse entity data for configuration type and date fallback
  const entityData = useMemo(() => {
    if (dataReceived?.entity?.data) {
      return parseEntityData(dataReceived.entity.data);
    }
    return null;
  }, [dataReceived?.entity?.data]);

  // Get updated time with fallbacks
  const getUpdatedTime = () => {
    // Try eventDate first
    if (dataReceived?.eventDate) {
      return convertDateToLocaleTz(dataReceived.eventDate, programTimezone, false);
    }

    // Try to get updated_at or updatedAt from entity data
    const fallbackDate =
      entityData?.updated_at || entityData?.updatedAt || entityData?.updateAt;

    if (fallbackDate) {
      return convertDateToLocaleTz(fallbackDate, programTimezone, false);
    }

    return "-";
  };

  const navigateToHistoryLogs = () => {
    // Check if the user came from the configuration tab
    const fromConfigurationTab =
      location.pathname?.includes("configuration") ||
      location.state?.from === "configuration" ||
      (typeof document !== "undefined" &&
        document.referrer?.includes("history-logs-configuration"));

    // Check if the user came from the content tab
    const fromContentTab =
      location.pathname?.includes("content") ||
      location.state?.from === "content" ||
      (typeof document !== "undefined" &&
        document.referrer?.includes("history-logs-content"));

    if (fromConfigurationTab) {
      // Navigate back to the configuration tab
      navigate(`/${paths.system_setting}/${paths.history_logs}-configuration`);
    } else if (fromContentTab) {
      // Navigate back to the content tab
      navigate(`/${paths.system_setting}/${paths.history_logs}-content`);
    } else {
      // Navigate back to the campaigns tab
      navigate(`/${paths.system_setting}/${paths.history_logs}`);
    }
  };

  return (
    <div className="container">
      <div className="wrap">
        <Breadcrumb
          breadcrumbs={[
            {
              label: "Program Settings",
              href: `../../../${paths.system_setting}`,
            },
            {
              label: "History Logs",
              href: `../../../${paths.system_setting}/${paths.history_logs}`,
            },
            {
              label: dataReceived?.title || "History Logs",
            },
          ]}
          activeIndex={1}
        />
        <div className="history-log-details-container">
          <div className="card">
            <h3>Update Details</h3>
            <SummaryField data={dataReceived?.note} label="What's updated" />
            <div className="flex">
              <SummaryField data={getUpdatedTime()} label="Updated Time" />
              <SummaryField
                data={
                  dataReceived?.user?.userName || dataReceived?.user?.gpid || "-"
                }
                label="Updated By"
              />
            </div>
            <SummaryField
              data={dataReceived?.changeMade}
              label="Reason for Update"
            />

            {dataReceived?.difference && dataReceived.difference.length > 0 && (
              <DifferenceDisplay
                differences={dataReceived.difference}
                entityData={entityData}
              />
            )}
          </div>
          <div className="card">
            <h3>
              {dataReceived?.entity?.type?.charAt(0)?.toUpperCase() +
                dataReceived?.entity?.type?.slice(1)}{" "}
              Info
            </h3>
            <div className="flex">
              <SummaryField
                data={dataReceived?.entity?.name}
                label={
                  dataReceived?.entity?.type?.charAt(0)?.toUpperCase() +
                  dataReceived?.entity?.type?.slice(1) +
                  " Name"
                }
              />
              <SummaryField
                data={formatCampaignType(dataReceived?.entity?.subType)}
                label={
                  dataReceived?.entity?.type?.charAt(0)?.toUpperCase() +
                  dataReceived?.entity?.type?.slice(1) +
                  " Type"
                }
              />
            </div>

            {dataReceived?.entity?.type === "campaign" && (
              <div className="flex">
                <SummaryField
                  data={campaignTitleShort}
                  label="Campaign Short Name"
                />
                <SummaryField data={dataReceived?.entity?.id} label="Campaign Id" />
              </div>
            )}

            {dataReceived?.entity?.type === "configuration" && entityData && (
              <div className="configuration-data-section">
                <h4>Configuration Data</h4>
                <div className="json-view-container">
                  <pre className="json-view">
                    {JSON.stringify(entityData, null, 2)}
                  </pre>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="button-container">
          <Button
            size="medium"
            text="Close"
            variant="secondary"
            className="mr-2"
            onClick={navigateToHistoryLogs}
          />
        </div>
      </div>
    </div>
  );
}
